import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-hot-toast";
import api from "@/api/axios";
import { API, fmtDate } from "@/App";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ChevronLeft, Download } from "lucide-react";

const StatTile = ({ label, value }) => (
  <div className="rounded-lg border p-4">
    <div className="text-xs font-medium uppercase text-muted-foreground">{label}</div>
    <div className="mt-1 text-2xl font-semibold">{value}</div>
  </div>
);

export default function TeamRosterReport() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .get(`${API}/reports/team-roster`)
      .then((res) => {
        if (!cancelled) setReport(res.data.data);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("Failed to fetch team roster report:", err);
        toast.error(err?.response?.data?.message || "Could not fetch team roster report.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const downloadCsv = async (section) => {
    setDownloading(section);
    try {
      const res = await api.get(`${API}/reports/team-roster`, {
        params: { format: "csv", section },
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = section === "register" ? "Staff_Register.csv" : "Team_Overview_Timeline.csv";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Failed to download report:", err);
      toast.error(err?.response?.data?.message || "Could not download report.");
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <Link
            to="/reports"
            className="mb-1 flex items-center gap-1 text-sm text-muted-foreground hover:underline"
          >
            <ChevronLeft size={14} /> Reports
          </Link>
          <h1>Team Overview & Staff Register</h1>
          <p>Current and former team members, monthly hourly-rate timeline, and rate-change history.</p>
        </div>
      </div>

      {loading ? (
        <p className="mt-6">Loading...</p>
      ) : !report ? (
        <p className="mt-6 text-sm text-muted-foreground">No team data found.</p>
      ) : (
        <Tabs defaultValue="overview" className="mt-6">
          <TabsList>
            <TabsTrigger value="overview">Overview & Timeline</TabsTrigger>
            <TabsTrigger value="register">Staff Register</TabsTrigger>
          </TabsList>

          <TabsContent value="overview">
            <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-5">
              <StatTile label="Active Now" value={report.summary.activeNow} />
              <StatTile label="Former / Completed" value={report.summary.former} />
              <StatTile label="Total People" value={report.summary.totalPeople} />
              <StatTile
                label="Avg Active Rate"
                value={report.summary.avgActiveRate ? `₹${report.summary.avgActiveRate}/hr` : "-"}
              />
              <StatTile label="Rates To Confirm" value={report.summary.ratesToConfirm} />
            </div>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-base">Team Overview & Timeline</CardTitle>
                <Button
                  size="sm"
                  onClick={() => downloadCsv("overview")}
                  disabled={downloading === "overview" || !report.people.length}
                >
                  <Download size={14} className="mr-1" />
                  {downloading === "overview" ? "Downloading..." : "Download CSV"}
                </Button>
              </CardHeader>
              <CardContent>
                {!report.people.length ? (
                  <p className="text-sm text-muted-foreground">No team members found.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="sticky left-0 bg-background">Name</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Join Date</TableHead>
                          <TableHead>Leave Date</TableHead>
                          {report.months.map((m) => (
                            <TableHead key={m.key} className="text-center">
                              {m.label} {m.year}
                            </TableHead>
                          ))}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {report.people.map((p) => (
                          <TableRow key={p.id}>
                            <TableCell className="sticky left-0 bg-background font-medium">
                              {p.name}
                            </TableCell>
                            <TableCell>
                              <Badge variant={p.status === "Active" ? "default" : "secondary"}>
                                {p.status}
                              </Badge>
                            </TableCell>
                            <TableCell>{fmtDate(p.join_date)}</TableCell>
                            <TableCell>{fmtDate(p.leave_date)}</TableCell>
                            {p.monthly.map((v, idx) => (
                              <TableCell key={report.months[idx].key} className="text-center">
                                {v === null ? "" : v === "TBC" ? (
                                  <span className="text-muted-foreground">TBC</span>
                                ) : (
                                  `₹${v}`
                                )}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))}
                        <TableRow className="bg-muted/50 font-semibold">
                          <TableCell className="sticky left-0 bg-muted/50">People active</TableCell>
                          <TableCell colSpan={3} />
                          {report.monthlyActiveCounts.map((c, idx) => (
                            <TableCell key={report.months[idx].key} className="text-center">
                              {c}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="register">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-base">Staff Register</CardTitle>
                <Button
                  size="sm"
                  onClick={() => downloadCsv("register")}
                  disabled={downloading === "register" || !report.register.length}
                >
                  <Download size={14} className="mr-1" />
                  {downloading === "register" ? "Downloading..." : "Download CSV"}
                </Button>
              </CardHeader>
              <CardContent>
                {!report.register.length ? (
                  <p className="text-sm text-muted-foreground">No staff found.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Name</TableHead>
                          <TableHead>Email</TableHead>
                          <TableHead>Role</TableHead>
                          <TableHead>Job Title</TableHead>
                          <TableHead>Join Date</TableHead>
                          <TableHead>Leave Date</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Current Rate</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {report.register.map((r) => (
                          <TableRow key={r.id}>
                            <TableCell className="font-medium">{r.name}</TableCell>
                            <TableCell>{r.email}</TableCell>
                            <TableCell>{r.role}</TableCell>
                            <TableCell>{r.job_title || "-"}</TableCell>
                            <TableCell>{fmtDate(r.join_date)}</TableCell>
                            <TableCell>{fmtDate(r.leave_date)}</TableCell>
                            <TableCell>
                              <Badge variant={r.status === "Active" ? "default" : "secondary"}>
                                {r.status}
                              </Badge>
                            </TableCell>
                            <TableCell>{r.current_rate ? `₹${r.current_rate}/hr` : "TBC"}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="mt-4">
              <CardHeader>
                <CardTitle className="text-base">Hourly Rate History</CardTitle>
              </CardHeader>
              <CardContent>
                {!report.rate_history.length ? (
                  <p className="text-sm text-muted-foreground">No rate changes recorded yet.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Employee</TableHead>
                          <TableHead>Effective From</TableHead>
                          <TableHead>Effective To</TableHead>
                          <TableHead>Rate</TableHead>
                          <TableHead>Increment</TableHead>
                          <TableHead>Increment %</TableHead>
                          <TableHead>Change Type</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {report.rate_history.map((h, idx) => (
                          <TableRow key={`${h.employee}-${h.rate_start}-${idx}`}>
                            <TableCell className="font-medium">{h.employee}</TableCell>
                            <TableCell>{h.rate_start ? fmtDate(h.rate_start) : "From the start"}</TableCell>
                            <TableCell>{h.rate_end ? fmtDate(h.rate_end) : "Current"}</TableCell>
                            <TableCell>₹{h.rate}/hr</TableCell>
                            <TableCell>{h.increment !== null ? `₹${h.increment}` : "-"}</TableCell>
                            <TableCell>{h.increment_pct !== null ? `${h.increment_pct}%` : "-"}</TableCell>
                            <TableCell>{h.change_type}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}
